import React, { useState, useEffect, useRef } from 'react';
import { convertUrlsToMarkdownLinks } from '../utils/textUtils';
import ReactMarkdown from 'react-markdown';
import {
  Box,
  Typography,
  Paper,
  Container,
  useTheme,
  useMediaQuery,
  TextField,
  IconButton,
  CircularProgress,
  Stack,
  Snackbar,
  Alert,
  Button,
  Chip,
  Divider,
  Fade,
  Collapse,
  Tooltip
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { Send as SendIcon } from '@mui/icons-material';
import { sendSupportMessage } from '../services/api';
import { useSelectedChatbot } from '../context/SelectedChatbotContext';

// Componente para manejar errores silenciosamente
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: false }; // No mostrar UI de error
  }

  componentDidCatch(error, errorInfo) {
    // Solo registrar en consola si no es un error de ResizeObserver
    if (!error.message?.includes('ResizeObserver')) {
      console.error('Error:', error);
      console.error('Error Info:', errorInfo);
    }
  }

  render() {
    return this.props.children;
  }
}

const Support = () => {
  const { selectedChatbot, availableAssistants } = useSelectedChatbot();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [currentMessage, setCurrentMessage] = useState('');
  const [messages, setMessages] = useState([]);
  const [chatId, setChatId] = useState(null);
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [showFaq, setShowFaq] = useState(true);
  const [showFaqSection, setShowFaqSection] = useState(false);
  const messagesEndRef = useRef(null);

  // Código fijo del chatbot de soporte
  const SUPPORT_CHATBOT_CODE = 'utemlugfwn';

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Efecto para hacer scroll cuando hay nuevos mensajes
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Preguntas frecuentes predefinidas organizadas por categorías
  const faqQuestions = [
    // Categoría: Asistente
    {
      category: "Asistente",
      questions: [
        "¿Cómo puedo entrenar mi asistente?",
        "¿Cómo mejoro las respuestas de mi chatbot?",
        "¿Hay un límite para las instrucciones?",
        "¿Cómo pruebo los cambios en las instrucciones?"
      ]
    },
    // Categoría: Personalización
    {
      category: "Personalización",
      questions: [
        "¿Cómo personalizo los colores del chatbot?",
        "¿Cómo cambio el mensaje de bienvenida?",
        "¿Como puedo cambiar de modo claro a oscuro?",
        "¿Cómo modifico el estilo visual?"
      ]
    },
    // Categoría: Implementación
    {
      category: "Implementación",
      questions: [
        "¿Cómo implemento el chatbot en mi web?",
        "¿Necesito conocimientos de programación?",
        "¿Funciona en dispositivos móviles?",
        "¿Puedo ver una demo del chatbot?"
      ]
    },
    // Categoría: Análisis
    {
      category: "Análisis",
      questions: [
        "¿Cómo interpreto las estadísticas?",
        "¿Cómo exporto las conversaciones?",
        "¿Qué significan las gráficas en Analíticas?",
        "¿Cómo filtro las conversaciones?"
      ]
    },
    // Categoría: Gestión
    {
      category: "Gestión",
      questions: [
        "¿Cómo añado nuevos usuarios?",
        "¿Cómo cambio mi contraseña?",
        "¿Qué roles de usuario existen?",
        "¿Cómo gestiono los partners?"
      ]
    }
  ];

  // Mensaje inicial al cargar la página
  useEffect(() => {
    setMessages([
      {
        role: 'assistant',
        content: 'Hola, soy el asistente de soporte de AI4Assistant. ¿En qué puedo ayudarte hoy? Puedo responder a tus preguntas sobre el funcionamiento del dashboard y sus características.'
      }
    ]);

    // Mostrar FAQ con efecto fade-in después de un breve retraso
    setTimeout(() => {
      setShowFaqSection(true);
    }, 300);
  }, []);

  // Función para manejar el clic en una pregunta predefinida
  const handleFaqClick = (question) => {
    handleSendMessage(question);
    setExpandedCategory(null);
  };

  // Función para expandir/colapsar una categoría
  const toggleCategory = (categoryIndex) => {
    setExpandedCategory(expandedCategory === categoryIndex ? null : categoryIndex);
  };

  // Función para mostrar/ocultar el panel de FAQ
  const toggleFaq = () => {
    setShowFaq(!showFaq);
  };

  const handleSendMessage = async (questionText = null) => {
    const messageText = questionText || currentMessage.trim();
    if (!messageText) return;

    if (!questionText) {
      setCurrentMessage('');
    }

    // Ocultar FAQ mientras se carga la respuesta
    setShowFaqSection(false);

    // Añadir mensaje del usuario
    const userMessage = { role: 'user', content: messageText };
    setMessages(prev => [...prev, userMessage]);

    // Añadir mensaje de "escribiendo..."
    const typingMessage = { role: 'assistant', content: 'Escribiendo...', isTyping: true };
    setMessages(prev => [...prev, typingMessage]);

    setIsSendingMessage(true);

    try {
      // Buscar el nombre del chatbot seleccionado
      const assistantObj = availableAssistants?.find(a => a.assistant === selectedChatbot);
      const chatbotName = assistantObj ? assistantObj.name : '';
      const response = await sendSupportMessage(messageText, chatId, chatbotName);

      // Remover el mensaje de "escribiendo..."
      setMessages(prev => prev.filter(msg => !msg.isTyping));

      if (response && response.message) {
        const botMessage = { role: 'assistant', content: response.message };
        setMessages(prev => [...prev, botMessage]);

        if (response.chatId) {
          setChatId(response.chatId);
        }
      } else {
        throw new Error('El servidor no envió una respuesta válida');
      }
    } catch (error) {
      // Remover el mensaje de "escribiendo..."
      setMessages(prev => prev.filter(msg => !msg.isTyping));

      console.error('Error al enviar mensaje:', error);

      // Mostrar mensaje de error más amigable
      const errorMessage = {
        role: 'assistant',
        content: error.message || 'Lo siento, ha ocurrido un error al procesar tu mensaje. Por favor, inténtalo de nuevo más tarde.',
        isError: true
      };

      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsSendingMessage(false);

      // Mostrar FAQ nuevamente con fade-in después de recibir la respuesta
      setTimeout(() => {
        setShowFaqSection(true);
      }, 300);
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <ErrorBoundary>
      <Container maxWidth={false}>
        <Box sx={{ py: 0 }}>
          <Paper
            elevation={3}
            sx={{
              p: { xs: 2.5, sm: 3 },
              mb: { xs: 2.5, sm: 3.5 },
              borderRadius: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5
            }}
          >
            <Typography variant="h3" component="h1" sx={{ color: theme.palette.text.primary, mb: 0.5, fontWeight: 800, letterSpacing: '-0.03em', fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
              Centro de Soporte
            </Typography>
            <Typography variant="body1" sx={{ color: theme.palette.text.secondary, fontWeight: 500, fontSize: '1rem', fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
              Bienvenido al Centro de Soporte de AI4Assistant. Aquí encontrarás ayuda para sacar el máximo provecho de tu dashboard.
            </Typography>
          </Paper>

          {/* Chat de Soporte */}
          <Paper
            elevation={3}
            sx={{
              borderRadius: 2,
              height: '700px',
              minHeight: '500px',
              maxHeight: '1000px',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              mb: 3,
              width: '100%'
            }}
          >
            <Box sx={{
              p: 0.1,
              borderBottom: `1px solid ${theme.palette.divider}`,
              backgroundColor: theme.palette.action.hover,
              position: 'sticky',
              top: 0,
              zIndex: 1,
              backdropFilter: 'blur(8px)'
            }}>
              <Typography variant="h7" sx={{ color: theme.palette.text.primary, fontWeight: 400, fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif', marginLeft: '10px' }}>
                Asistente de Soporte
              </Typography>
            </Box>

            <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
              <Stack spacing={1}>
                {messages.map((message, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start',
                    }}
                  >
                    <Paper
                      sx={{
                        maxWidth: '80%',
                        p: 1.5,
                        backgroundColor: message.role === 'user' ? theme.palette.primary.main : theme.palette.background.default,
                        color: message.role === 'user' ? theme.palette.primary.contrastText : theme.palette.text.primary,
                      }}
                    >
                      <Box sx={{ fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif', color: 'inherit' }}>
                        <ReactMarkdown components={{
                          p: ({ node, ...props }) => <p style={{ marginTop: '0.5em', marginBottom: '0.5em' }} {...props} />,
                          a: ({ node, ...props }) => <a style={{ color: theme.palette.primary.main, textDecoration: 'underline' }} target="_blank" rel="noopener noreferrer" {...props} />
                        }}>{convertUrlsToMarkdownLinks(message.content)}</ReactMarkdown>
                      </Box>
                    </Paper>
                  </Box>
                ))}

                {/* Mostrar preguntas frecuentes después de cada respuesta del asistente - Versión compacta */}
                {messages.length > 0 && messages[messages.length - 1].role === 'assistant' && showFaqSection && (
                  <Box sx={{ height: '5px' }}></Box> /* Espacio adicional antes de la caja de FAQ */
                )}
                {messages.length > 0 && messages[messages.length - 1].role === 'assistant' && showFaqSection && (
                  <Fade in={true} timeout={800}>
                    <Box sx={{
                      position: 'relative',
                      mt: 2,
                      mb: 1,
                      backgroundColor: theme.palette.background.paper,
                      borderRadius: 2,
                      border: `1px solid ${theme.palette.divider}`,
                      boxShadow: `0 0 10px ${theme.palette.action.selected}`,
                      maxWidth: '95%',
                      alignSelf: 'center'
                    }}>
                      {/* Botón para mostrar/ocultar FAQ */}
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          p: 1.2,
                          cursor: 'pointer',
                          backgroundColor: theme.palette.action.hover,
                          '&:hover': {
                            backgroundColor: theme.palette.action.selected,
                          },
                          borderRadius: '6px 6px 0 0',
                          borderBottom: `1px solid ${theme.palette.divider}`
                        }}
                        onClick={toggleFaq}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <HelpOutlineIcon sx={{ color: theme.palette.primary.main, mr: 1, fontSize: '1.1rem' }} />
                          <Typography variant="body2" sx={{ color: theme.palette.text.primary, fontWeight: 'medium', fontSize: '0.85rem' }}>
                            ¿Necesitas más ayuda? Preguntas frecuentes
                          </Typography>
                        </Box>
                        <IconButton size="small" sx={{ color: theme.palette.primary.main, p: 0.5, '&:hover': { color: theme.palette.text.primary, backgroundColor: theme.palette.action.selected } }}>
                          {showFaq ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                        </IconButton>
                      </Box>

                      <Collapse in={showFaq}>
                        <Box sx={{ p: 1.5, pt: 0 }}>
                          {/* Categorías como chips */}
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8, mt: 1, mb: 1 }}>
                            {faqQuestions.map((category, catIndex) => (
                              <Chip
                                key={catIndex}
                                label={category.category}
                                onClick={() => toggleCategory(catIndex)}
                                sx={{
                                  backgroundColor: expandedCategory === catIndex ? theme.palette.primary.main : theme.palette.background.default,
                                  color: expandedCategory === catIndex ? theme.palette.primary.contrastText : theme.palette.text.primary,
                                  border: `1px solid ${theme.palette.primary.main}`,
                                  '&:hover': {
                                    backgroundColor: theme.palette.primary.main,
                                    color: theme.palette.primary.contrastText,
                                    boxShadow: `0 0 8px ${theme.palette.action.selected}`,
                                    transform: 'translateY(-1px)'
                                  },
                                  cursor: 'pointer',
                                  fontSize: '0.75rem',
                                  height: '24px',
                                  transition: 'all 0.2s ease',
                                  fontWeight: expandedCategory === catIndex ? 'medium' : 'normal'
                                }}
                                size="small"
                              />
                            ))}
                          </Box>

                          {/* Preguntas de la categoría seleccionada */}
                          {expandedCategory !== null && (
                            <Collapse in={expandedCategory !== null}>
                              <Box sx={{ mt: 1, pl: 1 }}>
                                {faqQuestions[expandedCategory].questions.map((question, qIndex) => (
                                  <Tooltip key={qIndex} title="Haz clic para preguntar" placement="right">
                                    <Box
                                      sx={{
                                        p: 0.8,
                                        borderRadius: 1,
                                        cursor: 'pointer',
                                        '&:hover': {
                                          backgroundColor: theme.palette.action.hover,
                                          paddingLeft: '12px',
                                          transition: 'all 0.2s ease'
                                        },
                                        display: 'flex',
                                        alignItems: 'center',
                                        transition: 'all 0.2s ease',
                                        marginBottom: '2px'
                                      }}
                                      onClick={() => handleFaqClick(question)}
                                    >
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          color: theme.palette.text.primary,
                                          fontSize: '0.8rem',
                                          fontWeight: 'light',
                                          '&:hover': {
                                            color: theme.palette.primary.main
                                          },
                                          transition: 'color 0.2s ease'
                                        }}
                                      >
                                        {question}
                                      </Typography>
                                    </Box>
                                  </Tooltip>
                                ))}
                              </Box>
                            </Collapse>
                          )}
                        </Box>
                      </Collapse>
                    </Box>
                  </Fade>
                )}

                <div ref={messagesEndRef} />
              </Stack>
            </Box>

            <Box sx={{
              p: 2,
              borderTop: `1px solid ${theme.palette.divider}`,
              backgroundColor: theme.palette.action.hover,
              display: 'flex',
              gap: 1
            }}>
              <TextField
                fullWidth
                value={currentMessage}
                onChange={(e) => setCurrentMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Escribe tu pregunta aquí..."
                variant="outlined"
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: theme.palette.background.paper,
                    color: theme.palette.text.primary,
                    '& fieldset': {
                      borderColor: theme.palette.divider,
                    },
                    '&:hover fieldset': {
                      borderColor: theme.palette.primary.light,
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: theme.palette.primary.main,
                    },
                  },
                }}
              />
              <IconButton
                onClick={handleSendMessage}
                disabled={!currentMessage.trim() || isSendingMessage}
                sx={{
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.primary.contrastText,
                  '&:hover': {
                    backgroundColor: theme.palette.primary.dark,
                  },
                  '&.Mui-disabled': {
                    backgroundColor: theme.palette.action.hover,
                    color: theme.palette.text.secondary,
                  }
                }}
              >
                {isSendingMessage ? (
                  <CircularProgress size={24} sx={{ color: theme.palette.primary.contrastText }} />
                ) : (
                  <SendIcon />
                )}
              </IconButton>
            </Box>
          </Paper>
        </Box>
      </Container>
    </ErrorBoundary>
  );
};

export default Support;
